import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { IoLockClosedOutline } from "react-icons/io5";
import { toast } from "react-toastify";
import { useUser } from "../context/UserContext";
import api from "../utils/api";

function ChangePassword() {
  const [token, setToken] = useState(null); // null = session-based, string = token-based
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { setCurrentUser } = useUser();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tokenParam = params.get("token");

    if (tokenParam) {
      // Token-based reset (email link)
      setToken(tokenParam);
    } else {
      // Session-based reset (first login / profile)
      api
        .get("/api/current-user", { withCredentials: true })
        .then(() => {
          setToken(null); // explicit marker for session-based flow
        })
        .catch(() => {
          toast.error("You must be logged in to change your password.");
          navigate("/login");
        });
    }
  }, [location.search, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!newPassword || !confirmPassword) {
      toast.warning("Please fill in all fields.");
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.warning("Passwords do not match.");
      return;
    }

    try {
      setLoading(true);

      if (token !== null) {
        // Token-based flow (forgot password)
        await api.post("/reset-password", {
          token,
          newPassword,
        });
      } else {
        // Session-based flow (first login or profile password change)
        await api.post(
          "/change-password",
          { newPassword },
          { withCredentials: true }
        );
      }

      toast.success("Password changed successfully.");
      setCurrentUser(null); // clear context → force login again
      navigate("/login");
    } catch (error) {
      if (error.response?.data) {
        toast.error(error.response.data);
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen">
      {/* Left Section (Logo + message) */}
      <div className="sm:w-1/2 w-full bg-gradient-to-br from-slate-700 to-slate-900 text-white flex flex-col justify-center items-center relative overflow-hidden sm:grid hidden">
        <div className="z-10 text-center px-8">
           <motion.img
                      src="/companylogo.png"
                      alt="Value3D"
                      className="h-16 w-auto mb-6 mx-auto bg-white rounded-lg pr-2 pl-2"
                      initial={{ opacity: 0, y: 0 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.8 }}
                      whileHover={{ scale: 1.1 }}
                    />
          <motion.p
            className="text-lg text-slate-300"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            Enter your new password
          </motion.p>
        </div>
      </div>

      {/* Right Section (Form) */}
      <div className="sm:w-1/2 w-full flex justify-center items-center bg-gradient-to-br from-gray-50 to-gray-100 px-6">
        <motion.form
          onSubmit={handleSubmit}
          className="bg-white/80 backdrop-blur-xl p-8 sm:p-10 rounded-2xl shadow-xl w-full max-w-md space-y-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h2 className="text-3xl font-bold text-slate-800 mb-6">
            Change Password
          </h2>

          <div className="space-y-4">
            {/* New Password */}
            <div className="relative">
              <IoLockClosedOutline className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xl" />
              <input
                type="password"
                placeholder="New password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full border border-slate-200 pl-10 pr-4 py-3 rounded-full focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all duration-200"
              />
            </div>

            {/* Confirm Password */}
            <div className="relative">
              <IoLockClosedOutline className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xl" />
              <input
                type="password"
                placeholder="Confirm password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full border border-slate-200 pl-10 pr-4 py-3 rounded-full focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all duration-200"
              />
            </div>
          </div>

          {/* Submit Button */}
          <motion.button
            type="submit"
            disabled={loading}
            className={`w-full text-white py-3 rounded-full transition duration-200 transform ${
              loading
                ? "bg-slate-400 cursor-not-allowed"
                : "bg-slate-700 hover:bg-slate-800 hover:scale-[1.02] active:scale-[0.98]"
            }`}
            whileHover={!loading && { scale: 1.02 }}
            whileTap={!loading && { scale: 0.98 }}
          >
            {loading ? "Resetting..." : "Reset Password"}
          </motion.button>
        </motion.form>
      </div>
    </div>
  );
}

export default ChangePassword;
